import 'whatwg-fetch';

import {getScriptSettings} from '../common/settings';

const {
  ipData: {
    apiKey = '157b7f4c1610f747b2db9444cdaadcd230048bdb01428775a0d3611e'
  } = {}
} = getScriptSettings();

/**
 * @typedef {Object} MobileCarrier
 * @type Object
 * @property {string} name - The name of the Mobile Carrier.
 * @property {string} mcc - The Mobile Country Code of the carrier.
 * @property {string} mnc - The Mobile Network Code that identifies the carrier.
 */

/**
 * @typedef {Object} Language
 * @type Object
 * @property {string} name - The language name in English.
 * @property {string} native - The native name of the language.
 */

/**
 * @typedef {Object} Currency
 * @type Object
 * @property {string} name
 * @property {string} code - The ISO 4217 currency code.
 * @property {string} symbol
 * @property {string} native - The native symbol for the currency.
 * @property {string} plural - The plural version of the currency. For example: US dollars, Australian dollars, Euros.
 */

/**
 * @typedef {Object} TimeZone
 * @type Object
 * @property {string} name
 * @property {string} abbr - The abbreviation of the TimeZone. e.g. "PDT"
 * @property {string} offset - The UTC offset of the TimeZone
 * @property {boolean} is_dst - `true` if Daylight Savings is accounted for; otherwise `false`
 * @property {string} current_time - The exact current time in the Timezone the IP Address belongs to accounting for DST.
 */

/**
 * @typedef {Object} Threat
 * @type Object
 * @property {boolean} is_tor - `true` if IP Address is a known Tor exit node; otherwise, `false`.
 * @property {boolean} is_proxy - `true` if IP Address is a known proxy of any type; otherwise, `false`.
 * @property {boolean} is_anonymous - `true` if either `is_tor` or `is_proxy` are `true`; otherwise, `false`.
 * @property {boolean} is_known_attacker - `true` if the IP Address is a known (reported) source of malicious activity.
 * @property {boolean} is_known_abuser - `true` if the IP Address is a known (reported) source of abuse.
 * @property {boolean} is_threat - `true` if either `is_known_attacker` or `is_known_abuser`; otherwise, `false`.
 * @property {boolean} is_bogon - `true` if the IP Address is a Bogon -- i.e. an unassigned, unaddressable IP Address.
 */

/**
 * @typedef {Object} IPDataResponse
 * @type Object
 * @property {string} ip - The IP address that was looked up
 * @property {boolean} is_eu - `true` or `false` depending on whether the country is a recognized member of the European Union. The list of countries is compiled from [this]{@link https://europa.eu/european-union/about-eu/countries_en} list on the European Union website.
 * @property {number} count - The total number of requests made by your API Key in the last 24 hrs. Updated once a minute.
 * @property {string} city - The city name.
 * @property {string} region - The name of the region.
 * @property {string} region_code - The ISO 3166-2 code for the region.
 * @property {string} country_name - The name of the country.
 * @property {string} country_code - The ISO 3166-1 alpha-2 code for the country
 * @property {string} continent_name - The continent name
 * @property {string} continent_code - the 2  letter ISO 3166-1 alpha-2 code for the continent. One of: AF, AN, AS, EU, NA, OC, SA
 * @property {number} latitude - An approximate latitudinal location for the IP Address. Often near the center of population.
 * @property {number} longitude - An approximate longitudinal location for the IP Address. Often near the center of population.
 * @property {string} asn - The Autonomous System Number that references the IP Address's owning organization.
 * @property {string} organisation - The name of the organisation that owns the IP Address.  This will default to the ISP if the name of the organisation is not available.
 * @property {string} postal - The postal code for where the IP Address is located.
 * @property {string} calling_code - The International Calling Code for the country where the IP Address is located.
 * @property {string} flag - A link to a PNG file with the flag of the country where the IP Address is located.
 * @property {string} emoji_flag - An emoji version of the flag of the country where the IP Address is located.
 * @property {string} emoji_unicode - The Unicode for the emoji flag.
 * @property {MobileCarrier} carrier - the IP Address's mobile carrier details
 * @property {Language[]} languages
 * @property {Currency[]} currency
 * @property {TimeZone} time_zone
 * @property {Threat} threat
 */

/**
 * Gets IP Address Info for the current visitor.
 * @returns {Promise<IPDataResponse|null>}
 */
export async function fetchIpData() {
  const response = await fetch(
    `https://api.ipdata.co?api-key=${apiKey}`,
    {
      headers: {
        'Accept': 'application/json'
      }
    }
  );

  if (response.ok) {
    return await response.json();
  } else {
    console.error('ipdata request failed', {status: response.status, headers: response.headers});
    const responseText = await response.text();
    console.error(`ipdata failed request content: ${responseText}`);
    return null;
  }
}
